// subtasks/components/SubTaskStatusButtonsView.tsx
import Button from "devextreme-react/button";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import TextArea from "devextreme-react/text-area";
import ValidationGroup from "devextreme-react/validation-group";
import Validator, { RequiredRule, StringLengthRule } from "devextreme-react/validator";
import React from "react";
import { SubTaskStatusButtonsVM } from "../hooks/useSubTaskStatusButtonsController";
import { StepStatus } from "../../../../../dtos/StepStatus";
import "../SubTaskStatusButtons.css";

type Props = SubTaskStatusButtonsVM;

export default function SubTaskStatusButtonsView(vm: Props) {
    const {
        // state
        visible,
        isReadyDisabled,
        isCompleteDisabled,
        popupTitle,

        // perms
        canMarkReady,
        canMarkComplete,
        canRequestRevision,
        canApprove,

        // refs
        newCommentTextbox,
        newCommentButton,
        newCommentTextValidationGroup,

        // handlers
        handleActionClick,
        handleRevisionClick,
        cancelRevisionClick,
        hidePhaseComments,
        validateTextBox,
        addComment,

        // Individual button handlers
        handleMarkReadyClick,
        handleMarkCompleteClick,
        handleApproveClick,

        // None workflow specific
        isNoneWorkflow,
        handleComplete,

        // Template mode
        mode,
        handleDeleteSubtask,
    } = vm;

    const isTemplateMode = mode === "templates";

    const saveOptions = {
        text: "Yes",
        stylingMode: "outlined" as const,
        type: "normal" as const,
        onClick: async (e: any) => await addComment(e),
    };

    const cancelOptions = {
        text: "No",
        stylingMode: "outlined" as const,
        type: "normal" as const,
        onClick: async (_e: any) => cancelRevisionClick(),
    };

    return (
        <>
            <div className="stepStatusButtonContainer">
                {canMarkReady && (
                    <Button
                        disabled={isReadyDisabled}
                        icon="check"
                        stylingMode="contained"
                        hint="Set subtask status to ready"
                        text="Mark as Ready"
                        onClick={handleMarkReadyClick}
                    />
                )}

                {canMarkComplete && (
                    <Button
                        disabled={isCompleteDisabled}
                        icon="parentfolder"
                        stylingMode="contained"
                        hint="Complete subtask"
                        text="Mark as Complete"
                        onClick={handleMarkCompleteClick}
                    />
                )}

                {canRequestRevision && (
                    <Button
                        icon="return"
                        stylingMode="contained"
                        hint="Subtask needs revision"
                        text="Request Revision"
                        onClick={handleRevisionClick}
                    />
                )}

                {canApprove && (
                    <Button
                        icon="movetofolder"
                        stylingMode="contained"
                        hint="Approve subtask"
                        text="Approve"
                        onClick={handleApproveClick}
                    />
                )}

                {isTemplateMode && handleDeleteSubtask && vm.canDeleteSubtask && (
                    <Button
                        icon="trash"
                        stylingMode="contained"
                        hint="Delete subtask from template"
                        text="Delete Subtask"
                        onClick={handleDeleteSubtask}
                        type="danger"
                    />
                )}
            </div>

            <Popup
                height={240}
                hideOnOutsideClick={hidePhaseComments}
                onHiding={hidePhaseComments}
                title={popupTitle}
                visible={visible}
                width={750}
            >
                <div className="dx-field col-12" style={{ padding: "0 0 10px 10px" }}>
                    <div className="row">
                        <div className="dx-field col-12">
                            <ValidationGroup ref={newCommentTextValidationGroup}>
                                <TextArea
                                    height={70}
                                    maxLength={2000}
                                    onValueChanged={validateTextBox}
                                    placeholder="enter your revision request here (max characters: 2000)"
                                    ref={newCommentTextbox}
                                    width="98%"
                                >
                                    <Validator>
                                        <RequiredRule />
                                        <StringLengthRule
                                            max={2000}
                                            message="Max length for a comment is 2000 characters"
                                        />
                                    </Validator>
                                </TextArea>
                            </ValidationGroup>
                        </div>
                    </div>
                </div>

                <div className="dx-field col-12" style={{ padding: "0 0 20px 10px" }}>
                    <div className="row">
                        <div className="dx-field col-12">
                            Are you sure you want to set the subtask status to grantee pending revision? No
                            revision request is sent out until Revision is requested at the task level.
                        </div>
                    </div>
                </div>

                <ToolbarItem widget="dxButton" toolbar="bottom" location="after" options={saveOptions} />
                <ToolbarItem widget="dxButton" toolbar="bottom" location="after" options={cancelOptions} />
            </Popup>
        </>
    );
}
